/**
 * Timeline Header Component
 * Navigation, date range display, and action buttons
 */

import { CalendarPlus, ChevronLeft, ChevronRight, User } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Select, Typogaphy } from 'vnetwork-ui'

import {
    TimelineHeaderProps,
    WEEK_RANGE_OPTIONS,
    WeekRangeOption,
} from '../../../../types/timeline'
import { formatDateRangeDisplay } from '../utils/dateUtils'

const TimelineHeader: React.FC<TimelineHeaderProps> = ({
    dateRange,
    weekRange,
    onDateRangeChange: _onDateRangeChange,
    onWeekRangeChange,
    onNavigate,
    onTakeOncall,
    onGenerateAssignments,
    scheduleId: _scheduleId,
    scheduleName,
}) => {
    const { t: translate } = useTranslation('oncall')

    return (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-2">
            {/* Left side - Schedule info and action buttons */}
            <div className="flex flex-wrap items-center gap-2">
                {scheduleName && (
                    <div className="flex items-center gap-2 mr-4">
                        <Typogaphy className="font-medium text-primary">
                            {scheduleName}
                        </Typogaphy>
                        <span className="text-[10px] text-gray-500 capitalize bg-gray-100 px-2 py-1 rounded-full">
                            UTC +07:00
                        </span>
                    </div>
                )}

                {onGenerateAssignments && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<CalendarPlus className="w-4 h-4" />}
                        onClick={onGenerateAssignments}
                    >
                        {translate('generate_assignments')}
                    </Button>
                )}

                {onTakeOncall && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<User className="w-4 h-4" />}
                        onClick={onTakeOncall}
                    >
                        {translate('take_oncall')}
                    </Button>
                )}
            </div>

            {/* Right side - Date navigation */}
            <div className="flex items-center gap-3">
                {/* Date range display */}
                <Typogaphy
                    size="small"
                    className="font-medium text-gray-700 min-w-[180px] text-center capitalize"
                >
                    {formatDateRangeDisplay(dateRange)}
                </Typogaphy>

                {/* Navigation buttons */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onNavigate('prev')}
                        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors hover:text-primary cursor-pointer"
                        title={translate('previous')}
                    >
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button
                        onClick={() => onNavigate('next')}
                        className="p-1.5 rounded-full hover:bg-gray-100 transition-colors hover:text-primary cursor-pointer"
                        title={translate('next')}
                    >
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                </div>
                <div className="xs:w-full md:min-w-[120px] ">
                    <Select
                        size="small"
                        value={weekRange}
                        allowClear={false}
                        options={WEEK_RANGE_OPTIONS.map((option) => ({
                            label: translate(option.label),
                            value: option.value,
                        }))}
                        onChange={(value) =>
                            onWeekRangeChange(value as WeekRangeOption)
                        }
                    />
                </div>
            </div>
        </div>
    )
}

export default TimelineHeader
